import { useMemo, useState } from 'react'
import { RenameDocumentDialog } from '@/components/rename-document-dialog'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { ArrowUpDownIcon, DownloadIcon, EllipsisVerticalIcon, ExternalLinkIcon } from 'lucide-react'
import {
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	DEFAULT_LOCALE,
	DOCUMENT_STATUSES, DOCUMENT_STATUS_KEYS, DOCUMENT_STATUS_VARIANTS,
	DOCUMENT_TYPES, DOCUMENT_TYPE_KEYS,
} from '@/types'
import type { DocumentStatus, DocumentType, RegistryDocument } from '@/types'
import {
	useDeleteRentalDocumentMutation,
	useListDocumentsQuery,
	useUpdateRentalDocumentStatusMutation,
} from '@/api'

type SortField = 'title' | 'createdAt'
type SortDir = 'asc' | 'desc'

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface RentalDocumentsSectionProps {
	rentalId: string
}

export function RentalDocumentsSection({ rentalId }: RentalDocumentsSectionProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const currentHref = useRouterState({ select: (s) => s.location.href })
	const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null)
	const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all')
	const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')
	const [sortField, setSortField] = useState<SortField>('createdAt')
	const [sortDir, setSortDir] = useState<SortDir>('desc')

	const { data } = useListDocumentsQuery({
		rentalId,
		type:   typeFilter   === 'all' ? undefined : typeFilter,
		status: statusFilter === 'all' ? undefined : statusFilter,
	})
	const documents: RegistryDocument[] = data?.documents ?? []

	const [updateStatus] = useUpdateRentalDocumentStatusMutation()
	const [deleteDocument] = useDeleteRentalDocumentMutation()

	const sorted = useMemo(() => {
		const arr = [...documents]
		const dir = sortDir === 'asc' ? 1 : -1
		arr.sort((a, b) => {
			if (sortField === 'title') return a.title.localeCompare(b.title) * dir
			return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
		})
		return arr
	}, [documents, sortField, sortDir])

	function toggleSort(field: SortField) {
		if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		else {
			setSortField(field)
			setSortDir('asc')
		}
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<Select
					value={typeFilter}
					onValueChange={(v) => setTypeFilter(v as DocumentType | 'all')}
				>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('rent.documents.filters.type')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('rent.documents.filters.type')}</SelectItem>
						{DOCUMENT_TYPES.map((typ) => (
							<SelectItem key={typ} value={typ}>{t(DOCUMENT_TYPE_KEYS[typ])}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select
					value={statusFilter}
					onValueChange={(v) => setStatusFilter(v as DocumentStatus | 'all')}
				>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('rent.documents.filters.status')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('rent.documents.filters.status')}</SelectItem>
						{DOCUMENT_STATUSES.map((s) => (
							<SelectItem key={s} value={s}>{t(DOCUMENT_STATUS_KEYS[s])}</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="overflow-hidden rounded-(--radius-md) border border-(--color-border)">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead>
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className="size-3.5" />}
									onClick={() => toggleSort('title')}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
								>
									{t('rent.documents.columns.title')}
								</Button>
							</TableHead>
							<TableHead className="w-[180px]">
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className="size-3.5" />}
									onClick={() => toggleSort('createdAt')}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
								>
									{t('rent.documents.columns.createdAt')}
								</Button>
							</TableHead>
							<TableHead className="w-[140px] font-[500] text-(--color-muted-foreground)">
								{t('rent.documents.columns.status')}
							</TableHead>
							<TableHead className="w-[56px]" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.length === 0 ? (
							<TableRow>
								<TableCell colSpan={4} className="h-20 text-center text-sm text-(--color-muted-foreground)">
									{t('rent.documents.empty')}
								</TableCell>
							</TableRow>
						) : (
							sorted.map((doc) => (
								<TableRow key={doc.id} className="h-14 cursor-pointer" onClick={() => void navigate({ to: '/documents/$documentId', params: { documentId: doc.id }, search: { from: currentHref } })}>
									<TableCell className="px-4 text-sm font-[500] text-(--color-foreground)">
										{doc.title}
									</TableCell>
									<TableCell className="px-4 text-sm text-(--color-foreground)">
										{formatDate(doc.createdAt)}
									</TableCell>
									<TableCell className="px-4">
										<Badge variant={DOCUMENT_STATUS_VARIANTS[doc.status]} size="sm">
											{t(DOCUMENT_STATUS_KEYS[doc.status])}
										</Badge>
									</TableCell>
									<TableCell className="px-4" onClick={(e) => e.stopPropagation()}>
										<DropdownMenu>
											<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.documents.actions.menu')} />}>
												<EllipsisVerticalIcon className="size-4" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="w-auto min-w-40">
												{/* Opening means reading the document in the app, not
												    fetching the file again — this was byte-for-byte the
												    same handler as Скачать below, so both saved to disk.
												    Enabled without a fileUrl too: a document generated
												    from a template renders from its blocks. */}
												<DropdownMenuItem
													onClick={() => void navigate({ to: '/documents/$documentId', params: { documentId: doc.id }, search: { from: currentHref } })}
												>
													<ExternalLinkIcon />
													{t('rent.documents.actions.open')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={!doc.fileUrl}
													onClick={() => doc.fileUrl && window.open(doc.fileUrl, '_blank')}
												>
													<DownloadIcon />
													{t('rent.documents.actions.download')}
												</DropdownMenuItem>
												{/* A generated document is named after its template
												    and an uploaded one after the file, so neither name
												    says which document it is. Until now that was the
												    one thing about it that could not be corrected. */}
												<DropdownMenuItem
													onClick={() => setRenaming({ id: doc.id, title: doc.title })}
												>
													{t('documents.rename.action')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={doc.status !== 'DATA_FILLED'}
													onClick={() => void updateStatus({ id: doc.id, rentalId, body: { status: 'SIGNED_BY_ME' } })}
												>
													{t('deals.documents.actions.sign')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={doc.status !== 'SIGNED_BY_ME'}
													onClick={() => void updateStatus({ id: doc.id, rentalId, body: { status: 'SENT' } })}
												>
													{t('deals.documents.actions.send')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className="text-(--color-destructive) focus:text-(--color-destructive)"
													onClick={() => void deleteDocument({ id: doc.id, rentalId })}
												>
													{t('deals.documents.actions.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
			{renaming && (
				<RenameDocumentDialog
					document={renaming}
					onOpenChange={() => setRenaming(null)}
				/>
			)}
		</div>
	)
}
